import React from 'react';
import { Check, Circle, Plus } from 'lucide-react';
import { StoryGroup, SimpleStory } from '../services/simpleStore';
import { LanguageCode, LANGUAGE_NAMES } from '../types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';

interface LanguageSelectorProps {
  group: StoryGroup;
  onSelectLanguage: (storyId: string, language: LanguageCode) => void;
  onCreateTranslation?: (groupId: string, language: LanguageCode) => void;
  onClose: () => void;
}

// All languages in display order
const ALL_LANGUAGES: LanguageCode[] = ['eng', 'ps-AF', 'fa-AF'];

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  group,
  onSelectLanguage,
  onCreateTranslation,
  onClose,
}) => {
  const { stories, availableLanguages, missingLanguages, primaryStory } = group;

  const handleSelectLanguage = (lang: LanguageCode) => {
    const story = stories.find(s => s.language === lang);
    if (story) {
      onSelectLanguage(story.id, lang);
      onClose();
    }
  };

  const handleCreateTranslation = (lang: LanguageCode) => {
    if (onCreateTranslation) {
      onCreateTranslation(group.groupId, lang);
      onClose();
    }
  };

  // Check which stories have social posts
  const languagesWithSocialPosts = new Set(
    stories
      .filter(story => story.socialPosts &&
        (story.socialPosts.posts?.length > 0 || Object.keys(story.socialPosts).length > 0))
      .map(story => story.language)
  );

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Select Language</DialogTitle>
        </DialogHeader>

        {/* Story preview */}
        <div className="mb-4 p-3 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
          <p className="text-sm font-medium text-zinc-900 dark:text-white line-clamp-2">
            {primaryStory.headline || 'Untitled'}
          </p>
        </div>

        {/* Language list */}
        <div className="space-y-2">
          {ALL_LANGUAGES.map((lang) => {
            const isAvailable = availableLanguages.includes(lang);
            const story = stories.find(s => s.language === lang);
            const hasSocialPosts = languagesWithSocialPosts.has(lang);

            if (isAvailable && story) {
              // Existing language version
              // Phase 3: Updated status values
              const statusLabel =
                story.status === 'approved' ? 'Approved' :
                story.status === 'pending_review' ? 'Pending Review' :
                story.status === 'reviewed' ? 'Reviewed' :
                story.status === 'archived' ? 'Archived' :
                'Draft';

              const statusColor =
                story.status === 'approved' ? 'text-green-600 dark:text-green-400' :
                story.status === 'pending_review' ? 'text-blue-600 dark:text-blue-400' :
                story.status === 'reviewed' ? 'text-amber-600 dark:text-amber-400' :
                story.status === 'archived' ? 'text-zinc-400 dark:text-zinc-500' :
                'text-zinc-500 dark:text-zinc-400';

              return (
                <Button
                  key={lang}
                  onClick={() => handleSelectLanguage(lang)}
                  variant="outline"
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700"
                >
                  <div className="flex items-center gap-3">
                    <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <span className="font-medium">{LANGUAGE_NAMES[lang]}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasSocialPosts && (
                      <span className="text-xs text-green-600 dark:text-green-400">+ Social</span>
                    )}
                    <span className={`text-xs ${statusColor}`}>{statusLabel}</span>
                  </div>
                </Button>
              );
            } else {
              // Missing language - can create translation
              return (
                <Button
                  key={lang}
                  onClick={() => handleCreateTranslation(lang)}
                  variant="outline"
                  className="w-full flex items-center justify-between px-4 py-3 border-dashed opacity-60 hover:opacity-100 hover:bg-teal-50 dark:hover:bg-teal-900/20 hover:border-teal-300 dark:hover:border-teal-700"
                >
                  <div className="flex items-center gap-3">
                    <Circle className="h-4 w-4 text-zinc-400" />
                    <span>{LANGUAGE_NAMES[lang]}</span>
                  </div>
                  <span className="text-xs text-teal-600 dark:text-teal-400 flex items-center gap-1">
                    <Plus className="h-3 w-3" />
                    Create
                  </span>
                </Button>
              );
            }
          })}
        </div>

        <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-4 text-center">
          Click a language to edit, or create a new translation
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default LanguageSelector;
